import { useCallback, useMemo } from "react"
import { Alert, Card, Col, Form, Row } from "react-bootstrap"
import SaveDeleteButtons from "../../Components/default-components/buttons/SaveDeleteButtons"
import FormField from "../../Components/default-components/forms/FormField"
import { Switch } from "../../Components/default-components/forms/Switch"
import AutoComplete from "../../Components/default-components/forms/AutoComplete"
import PermissionContainer from "../../Hocs/withPermission"


/**
 * Componente de formulário de recurso 100% desacoplado
 * Todas as dependências são injetadas via props (IoC)
 */
function RecursoForm({
  // Dados do formulário
  initialData = null,
  
  // Callbacks de eventos
  onSave = null,
  onCancel = null,
  
  // Dependências injetadas (IoC)
  useRecursoService = null,        // Hook injetado 
  wsProvider = null,
  useToastService = null,
  useWebInterface = null,
  // Configurações de UI
  readOnly = false,
  showTitle = true,
  
  // Configurações de comportamento
  enableValidation = true,
  autoSave = false,
  confirmOnCancel = true
}) {



  // Usar o hook injetado para gerenciar o estado do formulário
  const {
    formData,
    errors,
    isLoading,
    isDirty,
    updateField,
    reset,
    submit,
    loadAgrupamentos
  } = useRecursoService({initialData, useToastService, useWebInterface, wsProvider, autoSave})

  const handleSubmit = useCallback(async () => {
    const success = await submit(onSave)
    return success
  }, [submit, onSave])

  const handleCancel = useCallback(() => {
    if (isDirty && confirmOnCancel) {
      const shouldCancel = window.confirm(
        'Você tem alterações não salvas. Deseja realmente cancelar?'
      )
      if (!shouldCancel) return
    }
    
    reset()
    if (onCancel) {
      onCancel()
    }
  }, [isDirty, reset, onCancel, confirmOnCancel])

  // Função para carregar agrupamentos via service

  // Funções do autocomplete de agrupamento
  const handleAgrupamentoSelect = useCallback((selectedOption) => {
    updateField('agrupamento', selectedOption)
  }, [updateField])

  const handleAgrupamentoChange = useCallback((value) => {
    updateField('agrupamento', value)
  }, [updateField])

  return (
    <PermissionContainer
      component={() => (
        <Card className="mb-4">
          {showTitle && (
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                {initialData ? 'Editar Recurso' : 'Novo Recurso'}
              </h5>
              <div className="d-flex gap-2">
                <Switch
                  label={formData.active ? "Ativo" : "Inativo"}
                  val={formData.active}
                  onSwitchChange={(value) => updateField('active', value)}
                  disabled={readOnly || isLoading}
                />
              </div>
            </Card.Header>
          )}
          
          <Card.Body>
            {Object.keys(errors).length > 0 && (
              <Alert variant="danger" className="mb-3">
                <strong>Corrija os seguintes erros:</strong>
                <ul className="mb-0 mt-2">
                  {Object.entries(errors).map(([field, error]) => (
                    <li key={field}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}

              <style jsx>{`
                .autocomplete-dropdown .listgroup-autocomplete {
                  position: absolute !important;
                  top: 100% !important;
                  left: 0 !important;
                  right: 0 !important;
                  z-index: 1050 !important;
                  margin-top: 0 !important;
                  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
                  border: 1px solid #ced4da !important;
                  border-radius: 0.375rem !important;
                  background: white !important;
                  max-height: 200px !important;
                  overflow-y: auto !important;
                }
              `}</style>
              <Row>
                <Col md={6}>
                  <FormField
                    label="Nome *"
                    type="text"
                    val={formData.nome}
                    onValueUpdate={(value) => updateField('nome', value)}
                    isInvalid={!!errors.nome}
                    feedback={errors.nome}
                    disabled={readOnly || isLoading}
                    maxLength={255}
                    placeholder="Digite o nome do recurso"
                    required
                  />
                </Col>
                
                <Col md={6}>
                  <div style={{ position: 'relative' }}>
                    <AutoComplete
                      title="Agrupamento"
                      placeH="Digite ou selecione um agrupamento"
                      loadFunc={loadAgrupamentos}
                      loadCondition={true}
                      value={formData.agrupamento}
                      onValueChanged={handleAgrupamentoChange}
                      onSelectedClick={handleAgrupamentoSelect}
                      minChars={0}
                      showListOnFocus={true}
                      disableComponent={readOnly || isLoading}
                      className={`mb-3 autocomplete-dropdown ${errors.agrupamento ? 'is-invalid' : ''}`}
                      cacheKey="agrupamentos"
                    />
                    {errors.agrupamento && (
                      <div className="invalid-feedback" style={{ display: 'block', marginTop: '-1rem' }}>
                        {errors.agrupamento}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Descrição *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={formData.descricao}
                      onChange={(e) => updateField('descricao', e.target.value)}
                      isInvalid={!!errors.descricao}
                      disabled={readOnly || isLoading}
                      maxLength={500}
                      placeholder="Digite uma descrição detalhada do recurso"
                      required
                    />
                    <Form.Text className="text-muted">
                      {formData.descricao.length}/500 caracteres
                    </Form.Text>
                    {errors.descricao && (
                      <Form.Control.Feedback type="invalid">
                        {errors.descricao}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {!readOnly && (
                <Row>
                  <Col md={12}>
                    <SaveDeleteButtons
                      saveClick={handleSubmit}
                      voltarClick={handleCancel}
                      disabled={!isDirty}
                      saveLabel={initialData ? "Atualizar" : "Criar"}
                      voltarLabel="Cancelar"
                    />
                  </Col>
                </Row>
              )}
          </Card.Body>
        </Card>
      )}
    />
  )
}

export default RecursoForm
